// components/ui/Fieldset.tsx
// A field group with an eyebrow label + input slot.
// Replaces the old <label> + <input className="..."> pattern in step components.

import React from 'react';

interface FieldsetProps {
    label: React.ReactNode;
    help?: string;
    error?: string;
    children: React.ReactNode;
    className?: string;
}

export function Fieldset({ label, help, error, children, className = '' }: FieldsetProps) {
    return (
        <div className={`mb-6 ${className}`}>
            <label className="block text-[11px] tracking-[0.14em] uppercase text-[#6B6762] mb-2 font-medium">
                {label}
            </label>
            {children}
            {help && !error && (
                <div className="mt-2 text-[12px] text-[#6B6762] leading-relaxed">{help}</div>
            )}
            {error && (
                <div className="mt-2 text-[12px] text-[#7A5A00] leading-relaxed flex items-start gap-2">
                    <span className="font-serif italic">!</span>
                    <span>{error}</span>
                </div>
            )}
        </div>
    );
}

// Serif-underlined input (Chambers style).
// Use inside <Fieldset> for names, dates, addresses.
export const TextInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
    function TextInput({ className = '', ...props }, ref) {
        return (
            <input
                ref={ref}
                {...props}
                className={`w-full py-3 px-0 border-0 border-b border-[#E8E4DB] bg-transparent
                            font-serif text-[19px] text-[#1A1A1A]
                            focus:border-[#3730A3] focus:outline-none transition-colors
                            ${className}`}
            />
        );
    }
);

// Boxed numeric input — for amounts, mono tabular.
export const NumericInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
    function NumericInput({ className = '', ...props }, ref) {
        return (
            <input
                ref={ref}
                {...props}
                className={`w-full py-3 px-4 border border-[#E8E4DB] bg-white
                            font-mono text-[15px] text-[#1A1A1A] tabular-nums
                            focus:border-[#3730A3] focus:outline-none transition-colors
                            ${className}`}
            />
        );
    }
);
